import { Minus, Plus } from 'lucide-react';
import { CATEGORIES, LOCATIONS, formatNPR, formatQty, formatSize, formatGauge } from '../lib/utils';
import { deriveFloor } from '../lib/pricing';
import { encodeAmount } from '../lib/cipher';

// Returns a 3-state stock status: out (0), low (<= threshold), ok
export function getStockStatus(product) {
  if (product.quantity <= 0) {
    return { key: 'out', label_en: 'Out', label_np: 'सकियो', badge: 'bg-red-100 text-red-700' };
  }
  if (product.quantity <= product.low_stock_threshold) {
    return { key: 'low', label_en: 'Low', label_np: 'कम', badge: 'bg-amber-100 text-amber-700' };
  }
  return { key: 'ok', label_en: 'OK', label_np: 'ठिक', badge: 'bg-green-100 text-green-700' };
}

export default function ProductCard({
  product,
  onClick,
  showStock = true,
  onStockChange,
  sizeUnits = {},
  units = {},
  shop = null,
}) {
  const category = CATEGORIES[product.category] || CATEGORIES.other;
  const location = LOCATIONS[product.location] || LOCATIONS.shelf_top;
  const status = getStockStatus(product);
  const approx = product.quantity_type === 'approximate' ? '~' : '';
  const size = formatSize(product, sizeUnits);
  const gauge = formatGauge(product.gauge);
  const qty = formatQty(product.quantity);
  // No +/- stepper for things measured rather than counted: a "+1" next to a
  // 12.5 kg pile of brass invites a wrong number. Those are edited on the form.
  const showStepper = Boolean(onStockChange) && !units[product.unit]?.allow_decimal;
  // The floor written in the shop's tag cipher, for checking against the label.
  // Empty until a code word is set in Settings, which is the guard used
  // everywhere else this is shown.
  const floorCode = encodeAmount(
    deriveFloor(product.cost_price, Number(shop?.min_margin_percent) || 10, product.min_price ?? null),
    shop?.cipher_keyword,
  );

  // Stepper handlers must not bubble up to the card's onClick (which navigates)
  const adjust = (e, delta) => {
    e.stopPropagation();
    const next = Math.max(0, (product.quantity || 0) + delta);
    if (next !== product.quantity) onStockChange?.(product, next);
  };

  return (
    <div
      onClick={() => onClick?.(product)}
      className="product-card"
      data-testid={`product-card-${product.id}`}
    >
      <div className="flex justify-between items-start mb-2">
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-[#2D2D2D] truncate">
            {product.name_en}
            {size && <span className="ml-1.5 font-nepali text-[#8B0000]">{size}</span>}
            {gauge && (
              <span className="ml-1.5 text-xs font-normal text-gray-500 font-nepali">
                {gauge}
              </span>
            )}
          </h3>
          {product.name_np && (
            <p className="text-sm text-gray-500 font-nepali truncate">
              {product.name_np}
              {size && <span className="ml-1.5">{size}</span>}
            </p>
          )}
        </div>

        {showStock && (
          <span
            className={`ml-2 px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${status.badge}`}
            data-testid={`stock-badge-${product.id}`}
          >
            {status.label_np} · {approx}{qty}
          </span>
        )}
      </div>

      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 min-w-0">
          <span
            className="px-2 py-0.5 rounded text-xs font-medium whitespace-nowrap"
            style={{ backgroundColor: `${category.color}20`, color: category.color }}
          >
            {category.name_np}
          </span>
          {product.sku && (
            <span className="text-xs font-mono text-gray-400">{product.sku}</span>
          )}
          {floorCode && (
            <span
              className="text-xs font-mono font-bold tracking-widest text-amber-700"
              title="Floor, in the shop's tag code"
              data-testid={`floor-code-${product.id}`}
            >
              {floorCode}
            </span>
          )}
          {product.brand && (
            <span className="text-xs text-gray-500 truncate">{product.brand}</span>
          )}
          <span className="text-xs text-gray-400 truncate">{location.name_np}</span>
        </div>

        <span className="font-bold text-[#8B0000] whitespace-nowrap">
          {formatNPR(product.mrp || 0)}
        </span>
      </div>

      {/* Inline quick stock adjust — only for whole-unit items */}
      {showStepper && (
        // Packed left on purpose: the fixed FAB sits over the card's bottom-right
        // corner, and a right-aligned stepper puts + directly under it.
        <div
          className="flex items-center justify-start gap-2 mt-3 pt-3 border-t border-gray-100"
          data-testid={`stock-stepper-${product.id}`}
        >
          {/* Steps by one piece. Deliberately not offered for things measured
              rather than counted -- a +1 kg button next to a 12.5 kg pile of
              brass invites a wrong number. Those are edited on the form. */}
          <span className="text-xs text-gray-400 mr-1 font-nepali">स्टक मिलाउनुहोस्</span>
          <button
            onClick={(e) => adjust(e, -1)}
            disabled={product.quantity <= 0}
            className="w-9 h-9 flex items-center justify-center rounded-lg bg-gray-100 text-gray-700 disabled:opacity-40 active:scale-95 transition"
            aria-label="Decrease stock"
            data-testid={`stock-minus-${product.id}`}
          >
            <Minus className="w-4 h-4" />
          </button>
          <span className="w-14 text-center font-semibold text-[#2D2D2D]">
            {approx}{qty}
          </span>
          <button
            onClick={(e) => adjust(e, 1)}
            className="w-9 h-9 flex items-center justify-center rounded-lg bg-gray-100 text-gray-700 active:scale-95 transition"
            aria-label="Increase stock"
            data-testid={`stock-plus-${product.id}`}
          >
            <Plus className="w-4 h-4" />
          </button>
        </div>
      )}
    </div>
  );
}
